import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { tap, shareReplay } from "rxjs/operators";

@Injectable()
export class ContractService {
  constructor(private http: HttpClientService) {}
  representativeCache: Observable<any>;
  bankAccountsCache: Observable<any>;

  public create(contract, sections, application_id, bank_account_id: number | string = null): Observable<any> {
    const params = {
      contract,
      sections,
      application_id,
      bank_account_id,
    };
    return this.http
      .post("/api/contracts", params, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("ContractService.receivedContract: Ok.")));
  }

  getMarca(id) {
    return this.http.get(`/api/marcas/${id}`).pipe(tap((_) => console.log("ContractService.getMarca: Ok.")));
  }

  getModelo(id) {
    return this.http.get(`/api/modelos/${id}`).pipe(tap((_) => console.log("ContractService.getModelo: Ok.")));
  }

  getVersion(id) {
    return this.http.get(`/api/versiones/${id}`).pipe(tap((_) => console.log("ContractService.getVersion: Ok.")));
  }

  getRepresentatives() {
    if (!this.representativeCache) {
      this.representativeCache = this.requestRepresentatives().pipe(shareReplay(1));
    }
    return this.representativeCache;
  }

  requestRepresentatives() {
    return this.http
      .get("/api/contracts/representatives")
      .pipe(tap((_) => console.log("ContractService.getRepresentatives: Ok.")));
  }

  getBankAccountsCache(application_id?) {
    if (!this.bankAccountsCache) {
      this.bankAccountsCache = this.getBankAccounts(application_id).pipe(shareReplay(1));
    }
    return this.bankAccountsCache;
  }

  getBankAccounts(application_id?) {
    const options = {
      params: {
        application_id,
      },
    };
    return this.http
      .get("/api/contracts/bank_accounts", options)
      .pipe(tap((_) => console.log("ContractService.getBankAccounts: Ok.")));
  }

  getApplicationContract(application_id: number, include_insurance_policy?: boolean) {
    const options: any = {};
    if (include_insurance_policy !== undefined) {
      options.params = {
        include_insurance_policy,
      };
    }
    return this.http
      .get(`/api/applications/${application_id}/contract`, options)
      .pipe(tap((_) => console.log("ContractService.getApplicationContract: Ok.")));
  }

  getContract(contract_id: number | string) {
    return this.http
      .get(`/api/contracts/${contract_id}`)
      .pipe(tap((_) => console.log("ContractService.getContract: Ok.")));
  }

  getVisitScheduleData(contract_id: number | string) {
    return this.http
      .get(`/api/contracts/${contract_id}/visit_schedule_data`)
      .pipe(tap((_) => console.log("ContractService.getVisitScheduleData: Ok.")));
  }

  getApplicationByContract(contract_id: number | string) {
    return this.http
      .get(`/api/contracts/${contract_id}/application`)
      .pipe(tap((_) => console.log("ContractService.getApplicationByContract: Ok.")));
  }

  generateRequest(application_id: number) {
    return this.http
      .post(`/api/applications/${application_id}/request_contract`, {})
      .pipe(tap((_) => console.log("ContractService.generateRequest: Ok.")));
  }

  updateContract(contract: any) {
    const options = {
      contract,
    };
    return this.http
      .put(`/api/contracts/${contract.id}`, options)
      .pipe(tap((_) => console.log("ContractService.updateContract: Ok.")));
  }

  pauseContract(id: number | string, tenure: number) {
    const params = {
      tenure,
    };
    return this.http
      .post(`/api/contracts/${id}/pause`, params)
      .pipe(tap((_) => console.log("ContractService.postPauseContract: Ok.")));
  }

  finishVisit(contract) {
    return this.http
      .post(`/api/contracts/${contract.id}/finish_visit`, { contract })
      .pipe(tap((_) => console.log("ContractService.finishVisit: Ok.")));
  }

  analystInfo(contract_id) {
    return this.http
      .post(`/api/contracts/${contract_id}/analyst_info`, null)
      .pipe(tap((_) => console.log("ContractService.analystInfo: Ok.")));
  }

  manipulateTimer(contract_id: string | number, params: any) {
    const options = {
      params,
    };
    return this.http
      .post(`/api/contracts/${contract_id}/manipulate_timer`, options)
      .pipe(tap((_) => console.log("ContractService.manipulateTimer: Ok.")));
  }

  finishReview(id, approved: boolean, comments = "", visitRejectionReason?: string) {
    const options: any = {
      approved,
      comments,
    };

    if (visitRejectionReason) {
      options.visit_rejection_reason = visitRejectionReason.toLowerCase();
    }

    return this.http
      .post(`/api/contracts/${id}/finish_review`, options)
      .pipe(tap((_) => console.log("ContractService.finishReview: Ok.")));
  }

  saveApprovalValidation(id: number, validationData: any): Observable<any> {
    return this.http
      .post(`/api/contracts/${id}/save_approval_validation`, validationData)
      .pipe(tap((_) => console.log("ContractService.saveApprovalValidation: Ok.")));
  }

  downloadInsurancePolicy(id: number) {
    return this.http
      .get(`/api/contracts/${id}/download_insurance_policy`, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("ContractService.downloadInsurancePolicy: Ok.")));
  }

  downloadContractAgain(id: number, sections: string[] = undefined) {
    const options = {
      responseType: "blob" as "json",
      params: {
        "sections[]": sections,
      },
    };
    return this.http
      .get(`/api/contracts/${id}/download_contract_again`, options)
      .pipe(tap((_) => console.log("ContractService.downloadContractAgain: Ok.")));
  }

  downloadSignedContract(id: number) {
    return this.http
      .get(`/api/contracts/${id}/download_signed_contract`, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("ContractService.downloadSignedContract: Ok.")));
  }

  uploadSignedContract(id: number) {
    return this.http
      .post(`/api/contracts/${id}/upload_signed_contract`, {})
      .pipe(tap((_) => console.log("ContractService.uploadSignedContract: Ok.")));
  }

  downloadBillingScheme(id: number, use_params_states?, siting_states?) {
    return this.http
      .get(`/api/contracts/${id}/download_billing_scheme`, {
        responseType: "blob" as "json",
        params: { "siting_states[]": siting_states, use_params_states },
      })
      .pipe(tap((_) => console.log("ContractService.downloadBillingScheme: Ok.")));
  }

  downloadExitLetter(account_id: number) {
    return this.http
      .get(`/api/accounts/${account_id}/download_exit_letter`, { responseType: "blob" as "json" })
      .pipe(tap((_) => console.log("ContractService.downloadExitLetter: Ok.")));
  }

  getAllContracts({
    status = undefined,
    person_search_param = undefined,
    search = undefined,
    analyst_id = undefined,
    page = undefined,
    page_size = undefined,
    created_at_start = undefined,
    created_at_end = undefined,
    expected_signature_date_start = undefined,
    expected_signature_date_end = undefined,
    signature_date_start = undefined,
    signature_date_end = undefined,
    generated_time_start = undefined,
    generated_time_end = undefined,
    disbursement_date_start = undefined,
    disbursement_date_end = undefined,
    vobo_time_start = undefined,
    vobo_time_end = undefined,
    gps_scheduled_start = undefined,
    gps_scheduled_end = undefined,
    gps_filter = undefined,
    filter_approved = undefined,
    filter_not_approved = undefined,
    filter_with_disbursement = undefined,
    provider_id = undefined,
    view_as = undefined,
    column = undefined,
    order = undefined,
    signature_type_filter = undefined,
  }) {
    const options = {
      params: {
        status,
        search,
        page,
        page_size,
        created_at_start,
        created_at_end,
        expected_signature_date_start,
        expected_signature_date_end,
        signature_date_start,
        signature_date_end,
        generated_time_start,
        generated_time_end,
        disbursement_date_start,
        disbursement_date_end,
        vobo_time_start,
        vobo_time_end,
        gps_scheduled_start,
        gps_scheduled_end,
        gps_filter,
        filter_approved,
        filter_not_approved,
        filter_with_disbursement,
        person_search_param,
        provider_id,
        column,
        order,
        signature_type_filter,
        "analyst_id[]": analyst_id,
        "view_as[]": view_as,
      },
    };
    return this.http
      .get("/api/contracts", options)
      .pipe(tap((_) => console.log("ContractService.getAllContracts: Ok.")));
  }

  getUsers() {
    const options = {};
    return this.http.get("/api/users", options).pipe(tap((_) => console.log("ContractService.getUsers: Ok.")));
  }

  clabeInfo(clabe) {
    const options = {
      params: {
        clabe,
      },
    };
    return this.http
      .get("/api/contracts/clabe_info", options)
      .pipe(tap((_) => console.log("ContractService.clabeInfo: Ok.")));
  }

  contractDetails(from, to) {
    const options = {
      responseType: "blob" as "json",
      params: {
        from,
        to,
      },
    };
    return this.http
      .get("/api/contracts/contract_details", options)
      .pipe(tap((_) => console.log("ContractService.contractDetails: Ok.")));
  }

  assignContract() {
    return this.http
      .get("/api/contracts/assign_contract")
      .pipe(tap((_) => console.log("ContractService.assignContract: Ok.")));
  }

  unassignedContracts() {
    return this.http
      .get("/api/contracts/unassigned_contracts")
      .pipe(tap((_) => console.log("ContractService.unassignedContracts: Ok.")));
  }

  notifySpecialist(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/contract_inconsistent_data`, params)
      .pipe(tap((_) => console.log("ContractService.notifySpecialist: Ok.")));
  }

  reportContractValidationIncident(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/contract_approval_validation_incidents`, params)
      .pipe(tap((_) => console.log("ContractService.reportContractValidationIncident: Ok.")));
  }

  requestVoBo(id: number | string, params?: any) {
    return this.http
      .post(`/api/contracts/${id}/request_vobo`, params || {})
      .pipe(tap((_) => console.log("ContractService.requestVoBo: Ok.")));
  }

  addDigitalSignatureIncident(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/add_digital_signature_incident`, params)
      .pipe(tap((_) => console.log("ContractService.addDigitalSignatureIncident: Ok.")));
  }

  notifySubsidyInconsistentData(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/notify_subsidy_inconsistent_data`, params)
      .pipe(tap((_) => console.log("ContractService.notifySubsidyInconsistentData: Ok.")));
  }

  saveProgress(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/save_progress`, params)
      .pipe(tap((_) => console.log("ContractService.saveProgress: Ok.")));
  }

  saveStepsAndDetails(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/save_steps_and_details`, params)
      .pipe(tap((_) => console.log("ContractService.saveStepsAndDetails: Ok.")));
  }

  public workflowAction(id: number | string, action: string, options?: any): Observable<any> {
    const params = {
      flux_action: action,
      options: options || {},
    };
    return this.http
      .post(`/api/contracts/${id}/workflow_action`, params)
      .pipe(tap((_) => console.log("ContractService.workflowAction: Ok.")));
  }

  saveVisitProgress(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/save_visit_progress`, params)
      .pipe(tap((_) => console.log("ContractService.saveVisitProgress: Ok.")));
  }

  savePolicyEmissionProgress(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/save_policy_emission_progress`, params)
      .pipe(tap((_) => console.log("ContractService.savePolicyEmissionProgress: Ok.")));
  }

  skipVisit(id: number | string, params): Observable<any> {
    return this.http
      .post(`/api/contracts/${id}/skip_visit`, params)
      .pipe(tap(() => console.log("ContractService.skipVisit: Ok.")));
  }

  updateContractParams(id: number | string, params) {
    return this.http
      .post(`/api/contracts/${id}/update_contract_params`, params)
      .pipe(tap((_) => console.log("ContractService.updateContractParams: Ok.")));
  }

  getParams(id: number | string) {
    return this.http
      .get(`/api/contracts/${id}/status_params`)
      .pipe(tap((_) => console.log("ContractService.getParams: Ok.")));
  }

  notifyOfficePolicy(id: number | string, policy_data) {
    const params = {
      policy_data,
    };
    return this.http
      .post(`/api/contracts/${id}/office_policy_notification`, params)
      .pipe(tap((_) => console.log("ContractService.notifyOfficePolicy: Ok.")));
  }

  notifyOfficeFrae(id: number | string, frae_data) {
    const params = {
      frae_data,
    };
    return this.http
      .post(`/api/contracts/${id}/office_frae_notification`, params)
      .pipe(tap((_) => console.log("ContractService.notifyOfficeFrae: Ok.")));
  }

  getContractByGpsId(gpsId: number | string) {
    return this.http
      .get(`/api/gps/${gpsId}/contract`)
      .pipe(tap((_) => console.log("ContractService.getContractByGpsId: Ok.")));
  }

  getParamsById(id_type: string, id: string): Observable<any> {
    const options = {
      params: {
        id_type,
        id,
      },
    };
    return this.http
      .get("/api/contracts/params_by_id", options)
      .pipe(tap((_) => console.log("ContractService.getParamsById")));
  }

  invoiceInfo(contract_id: number) {
    return this.http
      .post(`/api/contracts/${contract_id}/invoice_info`, null)
      .pipe(tap((_) => console.log("ContractService.invoiceInfo: Ok.")));
  }

  postNotifyInsufficientPayments(contract_id: number | string) {
    return this.http
      .post(`/api/contracts/${contract_id}/insufficient_payment`, null)
      .pipe(tap((_) => console.log("ContractService.postNotifyInsufficientPayments: Ok.")));
  }

  saveDisbursementOverpayments(contract_id: number | string, params) {
    return this.http
      .post(`/api/contracts/${contract_id}/save_disbursement_overpayments`, params)
      .pipe(tap((_) => console.log("ContractService.saveDisbursementOverpayments: Ok.")));
  }

  disbursementOverpayments(contract_id: number | string): Observable<any> {
    return this.http
      .get(`/api/contracts/${contract_id}/disbursement_overpayments`, null)
      .pipe(tap((_) => console.log("ContractService.disbursementOverpayments")));
  }

  downpaymentsDocuments(contract_id: number | string): Observable<any> {
    return this.http
      .get(`/api/contracts/${contract_id}/downpayments_documents`, null)
      .pipe(tap((_) => console.log("ContractService.downpaymentsDocuments")));
  }

  regenerateElectronicSignature(id: number | string): Observable<any> {
    return this.http
      .post(`/api/contracts/${id}/regenerate_electronic_signature`, null)
      .pipe(tap((_) => console.log("ContractService.regenerateElectronicSignature: Ok.")));
  }

  getContractGenerationProcessData(contract_id: number | string): Observable<unknown> {
    return this.http
      .get(`/api/contracts/${contract_id}/contract_generation_process`)
      .pipe(tap(() => console.log("ContractService.getContractGenerationProcess: Ok.")));
  }

  saveValidationBypasses(contract_id: number | string, bypass: any): Observable<any> {
    const params = { validation_bypasses: bypass };
    return this.http
      .post(`/api/contracts/${contract_id}/save_validation_bypasses`, params)
      .pipe(tap((_) => console.log("ContractService.saveValidationBypasses: Ok.")));
  }

  getContractEventLogs(contract_id: number | string, page?: number, per_page?: number): Observable<any> {
    const params: { [param: string]: string } = {};
    if (page !== undefined) {
      params.page = String(page);
    }
    if (per_page !== undefined) {
      params.per_page = String(per_page);
    }
    return this.http
      .get(`/api/contracts/${contract_id}/event_logs`, { params })
      .pipe(tap((_) => console.log("ContractService.getContractEventLogs: Ok.")));
  }
}
